'use client';
import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useAnalytics } from '../../Analytics';
import { useAnalyticsPageView } from '../../Analytics/AnalyticsPageView';
import { useStandardTrack } from '../../hooks/useStandardTrack';
import { renderTrackedHost, type TrackedHostProps } from '../trackedHost';

export type FeatureViewedProps = TrackedHostProps & {
  additionalAttributes?: object;
  contextCampaignUtmCampaign?: string;
  entityName: string;
  /** Maps to Element Viewed `section_name`. */
  entitySection: string;
  impressionPosition?: number;
  /** A single element to clone, or any content when `as` is set. */
  children: React.ReactNode;
};

export const FeatureViewed = forwardRef<HTMLElement, FeatureViewedProps>(
  (
    {
      additionalAttributes,
      contextCampaignUtmCampaign,
      entityName,
      entitySection,
      impressionPosition,
      children,
      ...host
    },
    ref,
  ) => {
    const track = useStandardTrack();
    const { deviceId } = useAnalytics();
    const { pageViewReady, pageType } = useAnalyticsPageView();
    const eventId = useMemo(() => crypto.randomUUID(), []);
    const hasFiredRef = useRef(false);
    const { ref: inViewRef, inView } = useInView({
      root: null,
      threshold: 0,
      rootMargin: '1px 0px 0px 0px',
      triggerOnce: true,
    });

    const setRefs = useCallback(
      (node: HTMLElement | null) => {
        if (ref && 'current' in ref) {
          ref.current = node;
        }
        inViewRef(node);
      },
      [inViewRef, ref],
    );

    useEffect(() => {
      if (pageViewReady && deviceId && inView && !hasFiredRef.current) {
        hasFiredRef.current = true;
        void track('Element Viewed', {
          entity_name: entityName,
          section_name: entitySection,
          event_id: eventId,
          ...(impressionPosition != null ? { item_position: impressionPosition } : {}),
          additional_attributes: {
            ...additionalAttributes,
            page_type: pageType,
            traditional_device_id: deviceId,
            ...(contextCampaignUtmCampaign
              ? { context_campaign_utm_campaign: contextCampaignUtmCampaign }
              : {}),
          },
        });
      }
    }, [
      pageViewReady,
      deviceId,
      inView,
      track,
      entityName,
      entitySection,
      eventId,
      impressionPosition,
      additionalAttributes,
      pageType,
      contextCampaignUtmCampaign,
    ]);

    return renderTrackedHost(host, children, setRefs, 'FeatureViewed');
  },
);
